import React from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';

/**
 * The step between "Publish" and a production agent changing its mind.
 *
 * Publishing guidance has no deploy, no review and no staging: the next
 * booking the agent classifies is classified under the new text. There is
 * nothing in the system that will catch a mistake here, so the only honest
 * thing the UI can do is say that out loud before it happens — which is this
 * dialog's whole job. It is NOT a formality to be skipped when the publish
 * button feels obvious enough; the button is deliberately not the action.
 *
 * There is ONE place a publish can start — the draft panel — because the
 * server refuses to publish a version that is already published. Going back
 * to an earlier version arrives here as the draft it was copied into, so this
 * dialog has one thing to say and says it once.
 */
function PublishInstructionsModal({
  show = false,
  scopeLabel,
  version,
  publishedVersion,
  publishing = false,
  onConfirm,
  onCancel,
}) {
  return (
    <Modal show={show} onHide={publishing ? undefined : onCancel} backdrop="static">
      <Modal.Header closeButton={!publishing}>
        <Modal.Title>{`Publish version ${version}?`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          <strong>
            {`This becomes the live guidance for ${scopeLabel} immediately.`}
          </strong>
          {' There is no deploy and no review step. The next thing the agent'
           + ' handles — a real booking, a real customer — it handles under'
           + ' this text.'}
        </p>

        <ul className="mb-3 ps-4">
          <li>
            {publishedVersion
              ? `Version ${publishedVersion} stops being live. It stays in the history.`
              : 'Nothing is live for this scope today. This is the first version to reach the agent.'}
          </li>
          <li>This publish is recorded in the history, under your name.</li>
          <li>Nothing is removed or rewritten. The history only ever grows.</li>
        </ul>

        <p className="text-muted small mb-0">
          To stop it again, publish a different version. There is no undo.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={publishing}>
          Cancel
        </Button>
        <Button variant="warning" onClick={onConfirm} disabled={publishing}>
          {publishing ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Publishing…
            </>
          ) : (
            <>
              <i className="fa fa-tower-broadcast me-2" aria-hidden="true" />
              Publish now — this goes live
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default PublishInstructionsModal;
